import React, { useRef, useEffect } from 'react';
import {
  TextField,
  useMediaQuery,
  ListSubheader,
  useTheme,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { Autocomplete, AutocompleteRenderGroupParams } from '@material-ui/lab';
import { VariableSizeList, ListChildComponentProps } from 'react-window';

const LISTBOX_PADDING = 8;

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = useRef<VariableSizeList>(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef<HTMLDivElement>(function ListboxComponent(
  props,
  ref,
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: React.ReactNode) => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const useStyles = makeStyles({
  root: {
    boxSizing: 'border-box',
    width: '100%',
    marginTop: 10,
    height: 40,
    backgroundColor: 'white',
    '& ul': {
      padding: 0,
      margin: 0,
    }
  },
  filterStyle: {
    boxSizing: 'border-box',
    width: '300px',
    marginRight: 10,
    // height: 40,
    backgroundColor: 'white',
    '& ul': {
      padding: 0,
      margin: 0,
    },
    '& .MuiOutlinedInput-input': {
      marginBottom: '-2px', // Adjust the padding to remove the top spacing
      fontSize: '14px', // Adjust the font size to decrease the size
    },
    '& .MuiInputLabel-root': {
      marginBottom: '-2px', // Adjust the padding to remove the top spacing
      fontSize: '14px', // Adjust the font size of the label
    },
    '& .MuiFormControl-root': {
      minHeight: '30px', // Adjust the width to decrease the size
      maxHeight: '30px', // Adjust the width to decrease the size
      margin: '0px 30px 10px 0px',
    },
  },

});

const renderGroup = (params: AutocompleteRenderGroupParams) => [
  <ListSubheader key={params.key} component="div">
    {params.group}
  </ListSubheader>,
  params.children,
];

interface IProps {
  options: { name: string }[];
  placeholder: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  label?: string;
  filter?: boolean;
}

const AutoCompleteField: React.FC<IProps> = ({
  options,
  placeholder,
  onChange,
  value = '',
  onBlur,
  disabled,
  label,
  filter
}) => {
  const classes = useStyles();

  const handleChange = (val: string) => {
    if (onChange) onChange(val);
  };

  const handleBlur = () => {
    if (onBlur) onBlur();
  };

  return (
    <Autocomplete
      disableListWrap
      className={filter ? classes.filterStyle: classes.root}
      placeholder={placeholder}
      ListboxComponent={
        ListboxComponent as React.ComponentType<React.HTMLAttributes<HTMLElement>>
      }
      renderGroup={renderGroup}
      options={options}
      getOptionLabel={(option) => option.name}
      onChange={(e, v) => handleChange(v?.name as string)}
      onBlur={handleBlur}
      value={{ name: value }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label ? label : undefined}
          size="small"
        />
      )}
      renderOption={(option) => <Typography noWrap>{option.name}</Typography>}
      getOptionSelected={(option, fieldVal) => option.name === fieldVal.name}
      noOptionsText="Nenhuma opção encontrada"
      disabled={disabled}
    />
  );
};

export default AutoCompleteField;
